/**
 * Firebase Authentication Module
 *
 * This module provides the core authentication functionality for the entire application.
 * It manages user authentication state, handles passwordless sign-in, and provides
 * authentication context to the rest of the application.
 *
 * Key features:
 * - Passwordless email link authentication
 * - User profile management integrated with Firestore
 * - Global auth state management via React Context
 * - Navigation handling for auth flows
 */

import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth'
import { createContext, useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { auth, firestore } from './index'
import { Alert } from '../../components'
import { useNavigate } from 'react-router-dom'
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
} from 'firebase/firestore'

/**
 * Handle passwordless sign-in functionality
 *
 * The application uses email link authentication (passwordless) as the primary auth method.
 * This enhances security and user experience by eliminating passwords.
 */

// Add rate limiting for sign-in attempts
const RATE_LIMIT_ATTEMPTS = 5
const RATE_LIMIT_WINDOW = 15 * 60 * 1000 // 15 minutes
const signInAttempts = new Map()

/**
 * Check if the user has exceeded rate limits
 * @param {string} email - User's email address
 * @returns {boolean} - True if rate limited, false otherwise
 */
function isRateLimited(email) {
  const now = Date.now()
  const attempts = signInAttempts.get(email) || []

  // Clean up old attempts
  const recentAttempts = attempts.filter(
    (timestamp) => now - timestamp < RATE_LIMIT_WINDOW
  )

  if (recentAttempts.length >= RATE_LIMIT_ATTEMPTS) {
    return true
  }

  // Update attempts
  recentAttempts.push(now)
  signInAttempts.set(email, recentAttempts)
  return false
}

/**
 * Global navigation function used for redirects after authentication
 * This allows non-component functions to perform navigation
 * @type {Function|null}
 */
let navigationFunction = null

/**
 * Sets the navigation function for use in non-component code
 * Called from App.js to provide the navigation capability to auth functions
 *
 * @param {Function} navigate - React Router's useNavigate hook result
 */
export const setNavigationFunction = (navigate) => {
  navigationFunction = navigate
}

/**
 * Handles passwordless sign-in via email link
 * This function is called when a user clicks on a sign-in link sent to their email
 *
 * Process:
 * 1. Verifies the URL contains a valid sign-in link
 * 2. Retrieves the email from localStorage or prompts the user
 * 3. Attempts to sign in with Firebase using the email and current URL
 * 4. Creates user profile if this is a new sign-up
 * 5. Handles navigation to the appropriate page post-authentication
 *
 * @returns {Promise<boolean>} - True if sign-in was successful, false otherwise
 */
export async function passwordlessSignin() {
  // Check if Firebase is available
  if (!auth || !firestore) {
    console.error('Authentication service is not available')
    toast.error('Authentication service is not available')
    return false
  }

  // Check if the current URL is a sign-in link
  if (!isSignInWithEmailLink(auth, window.location.href)) return false

  // Retrieve stored email and signup data (if available)
  let email = localStorage.getItem('KET-email-for-sign-in')
  let signupData = localStorage.getItem('KET-signup-data')

  // If email is not in localStorage, prompt the user to enter it
  if (!email) {
    const { value: inputEmail } = await Alert.fire({
      title: 'Enter your email address',
      input: 'email',
      inputLabel: 'Your email address',
      inputPlaceholder: 'Enter your email address',
      text: 'A secure sign-in link will be sent to your email address. Please check your inbox (and spam folder) after submitting.',
      validationMessage: 'Please enter a valid email',
      showClass: {
        popup: 'animate__animated animate__fadeInDown',
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOutUp',
      },
    })

    // Validate email input with proper regex
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    if (!inputEmail || !emailRegex.test(inputEmail.trim())) {
      toast.error('Please enter a valid email address')
      return false
    }
    email = inputEmail
  }

  // Check rate limiting
  if (isRateLimited(email)) {
    toast.error('Too many sign-in attempts. Please try again in 15 minutes.')
    return false
  }

  try {
    // Attempt to sign in with the email link
    const { user } = await signInWithEmailLink(
      auth,
      email,
      window.location.href
    )

    // If this is a new signup, create a user profile
    if (signupData && firestore) {
      let profileData
      try {
        profileData = JSON.parse(signupData)
      } catch (parseError) {
        console.warn('Invalid signup data format, using defaults')
        profileData = {}
      }

      // Sanitize and validate profile data
      const sanitizedName = (profileData.name || '').trim().slice(0, 100)
      const sanitizedBio = (profileData.bio || '').trim().slice(0, 500)
      const sanitizedRegion = (profileData.region || '').trim().slice(0, 100)
      const sanitizedCity = (profileData.city || '').trim().slice(0, 100)
      const sanitizedEdSystem = (profileData.edSystem || '')
        .trim()
        .slice(0, 100)
      const sanitizedLevel = (profileData.level || '').trim().slice(0, 50)
      const sanitizedSchool = (profileData.school || '').trim().slice(0, 200)

      const userProfileData = {
        uid: user.uid,
        fullName: sanitizedName,
        email: user.email,
        bio: sanitizedBio,
        region: sanitizedRegion,
        city: sanitizedCity,
        edSystem: sanitizedEdSystem,
        level: sanitizedLevel,
        school: sanitizedSchool,
        subjects: Array.isArray(profileData.subjects)
          ? profileData.subjects
              .slice(0, 10)
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        roles: { student: true }, // Default role
        profileCompleted: false,
        createdAt: new Date(),
        lastUpdated: new Date(),
        lastSignIn: new Date(),
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
        },
      }

      // Store the user profile in Firestore
      try {
        await addDoc(collection(firestore, 'userProfiles'), userProfileData)
      } catch (firestoreError) {
        console.warn('Failed to create user profile:', firestoreError)
        // Continue with auth flow even if profile creation fails
      }
      if (navigationFunction) {
        // Redirect new users to complete their profile
        navigationFunction('/dashboard/profile?newUser=true')
      }
    } else {
      // Update last sign-in time for existing users
      try {
        const userProfileRef = collection(firestore, 'userProfiles')
        const q = query(userProfileRef, where('uid', '==', user.uid))
        const querySnapshot = await getDocs(q)

        if (!querySnapshot.empty) {
          const profileDoc = querySnapshot.docs[0]
          await updateDoc(profileDoc.ref, {
            lastSignIn: new Date(),
            deviceInfo: {
              userAgent: navigator.userAgent,
              platform: navigator.platform,
              language: navigator.language,
            },
          })
        }
      } catch (updateError) {
        console.warn('Failed to update last sign-in time:', updateError)
      }

      if (navigationFunction) {
        // Redirect existing users to the dashboard
        navigationFunction('/dashboard/home')
      }
    }

    toast.success('Signed in successfully!')
    return true
  } catch (error) {
    // Log detailed error for debugging (server-side only in production)
    console.warn('Authentication failed:', error.code || 'unknown_error')

    // Show user-friendly error messages without exposing technical details
    let userMessage = 'Unable to sign in. Please try again.'
    if (error.code === 'auth/invalid-email') {
      userMessage = 'Please check your email address.'
    } else if (error.code === 'auth/invalid-action-code') {
      userMessage = 'This sign-in link has expired. Please request a new one.'
    }

    toast.error(userMessage)
    return false
  } finally {
    // Clean up localStorage regardless of outcome
    localStorage.removeItem('KET-email-for-sign-in')
    localStorage.removeItem('KET-signup-data')
  }
}

/**
 * Authentication Context System
 *
 * Provides the authentication state to the entire application using React Context.
 * This allows any component to access the current user's authentication state
 * and profile information without prop drilling.
 */

/**
 * Auth Context - Stores and provides authentication state
 * Contains the current authenticated user, their profile, and loading state
 *
 * @type {React.Context}
 */
const AuthContext = createContext({
  authUser: null,
  userProfile: null,
  isLoading: true,
})

/**
 * Custom hook to access the authentication context
 * Used throughout the application to check auth state and user profile
 *
 * @returns {Object} Auth context object with authUser, userProfile, and isLoading
 */
export const useAuth = () => useContext(AuthContext)

/**
 * Authentication Provider Component
 *
 * Wraps the application with authentication state management
 * Listens to Firebase auth state changes and synchronizes with user profiles in Firestore
 *
 * This component is a critical part of the application infrastructure:
 * - Provides auth state to all components
 * - Handles user profile synchronization
 * - Manages loading states during auth operations
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with auth context
 * @returns {JSX.Element} Provider component
 */
export const AuthUserProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    // Only set up auth listener if Firebase is available
    if (!auth || !firestore) {
      setIsLoading(false)
      return
    }

    // Subscribe to Firebase auth state changes
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user && firestore) {
        // User is signed in, fetch their profile from Firestore
        try {
          const userProfileRef = collection(firestore, 'userProfiles')
          const q = query(userProfileRef, where('uid', '==', user.uid))
          const querySnapshot = await getDocs(q)

          if (querySnapshot.empty) {
            // No profile exists, set default profile with verified STUDENT status
            // Note: All new users start as STUDENT and must be manually upgraded by admins
            setUserProfile({
              uid: user.uid,
              email: user.email,
              status: 'STUDENT', // Default, verified status
              displayName: user.displayName || user.email,
              profileCompleted: false,
              createdAt: new Date(),
              verified: false, // Requires admin verification for status changes
            })
          } else {
            // Profile exists, set it in state
            const profileData = querySnapshot.docs[0].data()
            setUserProfile(profileData)
          }
          setAuthUser(user)
        } catch (firestoreError) {
          console.warn('Failed to fetch user profile:', firestoreError)
          // Set basic profile without Firestore data
          setUserProfile({
            uid: user.uid,
            email: user.email,
            status: 'STUDENT',
            displayName: user.displayName || user.email,
            profileCompleted: false,
            createdAt: new Date(),
            verified: false,
          })
          setAuthUser(user)
        }
      } else if (user && !firestore) {
        // User authenticated but Firestore not available
        console.warn('User authenticated but Firestore not available')
        setUserProfile({
          uid: user.uid,
          email: user.email,
          status: 'STUDENT',
          displayName: user.displayName || user.email,
          profileCompleted: false,
          createdAt: new Date(),
          verified: false,
        })
        setAuthUser(user)
      } else {
        // User is signed out
        setAuthUser(null)
        setUserProfile(null)
      }
      setIsLoading(false)
    })

    // Cleanup subscription on unmount
    return () => unsubscribe()
  }, [navigate])

  return (
    <AuthContext.Provider value={{ authUser, userProfile, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}
